/**
 * The endpoint registry — one machine-readable table of the whole API surface.
 *
 * Both consumers read from here rather than restating routes:
 *
 *  - the gateway mounts handlers and validates with `descriptor.request`;
 *  - the front-end client builds URLs and parses with `descriptor.response`.
 *
 * A route that exists in only one of those two places is the failure this table
 * prevents. It is also the *sole-gateway* statement in machine-readable form: if a
 * path is not here, the React app has no business calling it, and there is no
 * other path to data (CLAUDE.md rule 11).
 *
 * `request` folds path parameters, query parameters and body into one object. The
 * transport split is per-endpoint and stated in each `notes` field; keeping one
 * validated object means the gateway and the client cannot disagree about which
 * half a field belongs to.
 */
import { SearchRequestSchema, SearchResponseSchema, SearchSelectionRequestSchema, SearchSelectionResponseSchema, } from './search.js';
import { RetrieveContractRequestSchema, RetrieveContractResponseSchema, } from './retrieve-contract.js';
import { UploadOptionsRequestSchema, UploadOptionsResponseSchema, UploadRequestSchema, UploadResponseSchema, } from './upload.js';
import { ManageClauseRequestSchema, ManageClauseResponseSchema } from './manage-clause.js';
import { ResolvePartyRequestSchema, ResolvePartyResponseSchema } from './resolve-party.js';
import { VerificationQueueRequestSchema, VerificationQueueResponseSchema, VerifyFieldRequestSchema, VerifyFieldResponseSchema, } from './verify-field.js';
import { VerifyFieldsBulkRequestSchema, VerifyFieldsBulkResponseSchema } from './verify-fields-bulk.js';
import { AcknowledgeNotificationRequestSchema, AcknowledgeNotificationResponseSchema, NotificationListRequestSchema, NotificationListResponseSchema, } from './acknowledge-notification.js';
import { PlanSharepointIngestRequestSchema, PlanSharepointIngestResponseSchema, } from './plan-sharepoint-ingest.js';
import { ExecuteSharepointIngestRequestSchema, ExecuteSharepointIngestResponseSchema, } from './execute-sharepoint-ingest.js';
import { ReopenSharepointDocumentRequestSchema, ReopenSharepointDocumentResponseSchema, SkipSharepointDocumentRequestSchema, SkipSharepointDocumentResponseSchema, } from './skip-sharepoint-document.js';
import { ClauseCandidateDecisionRequestSchema, ClauseCandidateDecisionResponseSchema, ClauseCandidateDetailRequestSchema, ClauseCandidateDetailResponseSchema, ClauseCandidateListRequestSchema, ClauseCandidateListResponseSchema, } from './clause-candidates.js';
import { UploadBundleExecuteRequestSchema, UploadBundleExecuteResponseSchema, UploadBundlePlanRequestSchema, UploadBundlePlanResponseSchema, } from './upload-bundle.js';
import { ClauseDetailRequestSchema, ClauseDetailResponseSchema, ClauseListRequestSchema, ClauseListResponseSchema, } from './clauses.js';
import { ManagePartyAliasRequestSchema, ManagePartyAliasResponseSchema, } from './manage-party-alias.js';
import { ObligationOwnersRequestSchema, ObligationOwnersResponseSchema, OutboundHealthRequestSchema, OutboundHealthResponseSchema, SearchWeaknessRequestSchema, SearchWeaknessResponseSchema, StaleJudgementsRequestSchema, StaleJudgementsResponseSchema, } from './quality.js';
import { SearchBoostListRequestSchema, SearchBoostListResponseSchema, SearchJudgementListRequestSchema, SearchJudgementListResponseSchema, SearchStabilityRequestSchema, SearchStabilityResponseSchema, } from './search-quality.js';
import { SearchAliasListRequestSchema, SearchAliasListResponseSchema, SearchAliasMineRequestSchema, SearchAliasMineResponseSchema, } from './search-aliases.js';
import { ReconcileCorpusRequestSchema, ReconcileCorpusResponseSchema } from './reconcile-corpus.js';
import { ClauseMatchListRequestSchema, ClauseMatchListResponseSchema } from './clause-matches.js';
import { PipelineStatusRequestSchema, PipelineStatusResponseSchema } from './pipeline-status.js';
import { ContractTagSuggestRequestSchema, ContractTagSuggestResponseSchema, ManageContractTagsRequestSchema, ManageContractTagsResponseSchema, } from './contract-tags.js';
import { RerunPipelineContractRequestSchema, RerunPipelineContractResponseSchema, } from './pipeline-rerun.js';
import { RegisterVendorPartyRequestSchema, RegisterVendorPartyResponseSchema, } from './register-vendor-party.js';
import { AnalyticsContractsListRequestSchema, AnalyticsContractsListResponseSchema, AnalyticsDashboardRequestSchema, AnalyticsDashboardResponseSchema, AnalyticsOverviewRequestSchema, AnalyticsOverviewResponseSchema, AnalyticsRenewalsDueRequestSchema, AnalyticsRenewalsDueResponseSchema, } from './analytics.js';
import { ApprovePipelineRunResponseSchema, CancelPipelineRunResponseSchema, GetPipelineRunResponseSchema, GetPipelineScheduleRequestSchema, GetPipelineScheduleResponseSchema, ListPipelineRunsRequestSchema, ListPipelineRunsResponseSchema, RequestPipelineRunRequestSchema, RequestPipelineRunResponseSchema, RunIdRequestSchema, RunPipelineNowRequestSchema, RunPipelineNowResponseSchema, SetPipelineScheduleRequestSchema, SetPipelineScheduleResponseSchema, } from './pipeline-runs.js';
import { SetPartyRegionRequestSchema, SetPartyRegionResponseSchema } from './set-party-region.js';
import { ListCloudeqEntitiesRequestSchema, ListCloudeqEntitiesResponseSchema } from './list-cloudeq-entities.js';
import { RescanCloudeqEntityRequestSchema, RescanCloudeqEntityResponseSchema } from './rescan-cloudeq-entity.js';
import { ApproveClauseMiningDryRunResponseSchema, CancelClauseMiningDryRunResponseSchema, ClauseMiningDryRunIdRequestSchema, GetClauseMiningDryRunResponseSchema, ListClauseMiningDryRunsRequestSchema, ListClauseMiningDryRunsResponseSchema, RequestClauseMiningDryRunRequestSchema, RequestClauseMiningDryRunResponseSchema, RunClauseMiningDryRunNowRequestSchema, RunClauseMiningDryRunNowResponseSchema, } from './clause-mining-dry-runs.js';
import { SetContractCloudeqEntityRequestSchema, SetContractCloudeqEntityResponseSchema, } from './set-contract-cloudeq-entity.js';
import { FieldAccuracyRequestSchema, FieldAccuracyResponseSchema } from './field-accuracy.js';
import { ListBaseContractsRequestSchema, ListBaseContractsResponseSchema, NominateBaseContractRequestSchema, NominateBaseContractResponseSchema, } from './base-contracts.js';
import { GetBaseDeviationReportRequestSchema, GetBaseDeviationReportResponseSchema, ListBaseDeviationReportsRequestSchema, ListBaseDeviationReportsResponseSchema, RunBaseDeviationReportRequestSchema, RunBaseDeviationReportResponseSchema, } from './base-deviation-reports.js';
import { ChangeExtractionFieldRequestSchema, ChangeExtractionFieldResponseSchema, ListExtractionFieldsRequestSchema, ListExtractionFieldsResponseSchema, } from './extraction-fields.js';
import { ContractTypeProposalsRequestSchema, ContractTypeProposalsResponseSchema, } from './contract-type-proposals.js';
import { DecideContractTypeRequestSchema, DecideContractTypeResponseSchema } from './decide-contract-type.js';
import { DismissAmendmentSignalRequestSchema, DismissAmendmentSignalResponseSchema, } from './dismiss-amendment-signal.js';
import { CosignFieldVerificationPolicyRequestSchema, CosignFieldVerificationPolicyResponseSchema, CosignVerificationSettingsRequestSchema, CosignVerificationSettingsResponseSchema, ProposeVerificationSettingsRequestSchema, ProposeVerificationSettingsResponseSchema, SelfCertifyFieldVerificationPolicyRequestSchema, SelfCertifyFieldVerificationPolicyResponseSchema, SelfCertifyVerificationSettingsRequestSchema, SelfCertifyVerificationSettingsResponseSchema, SetFieldVerificationPolicyRequestSchema, SetFieldVerificationPolicyResponseSchema, VerificationPolicyRequestSchema, VerificationPolicyResponseSchema, VerificationSettingsRequestSchema, VerificationSettingsResponseSchema, WithdrawVerificationSettingsRequestSchema, WithdrawVerificationSettingsResponseSchema, } from './verification-policy.js';
import { ExecuteVendorImportRequestSchema, ExecuteVendorImportResponseSchema, ListVendorImportsRequestSchema, ListVendorImportsResponseSchema, PlanVendorImportRequestSchema, PlanVendorImportResponseSchema, } from './vendor-import.js';
import { ContractLinksRequestSchema, ContractLinksResponseSchema, SetContractLinkRequestSchema, SetContractLinkResponseSchema, } from './contract-links.js';
import { ContractTypeAutoApprovalRequestSchema, ContractTypeAutoApprovalResponseSchema, SetContractTypeAutoApprovalRequestSchema, SetContractTypeAutoApprovalResponseSchema, } from './contract-type-auto-approval.js';
import { FieldApplicabilityByTypeRequestSchema, FieldApplicabilityByTypeResponseSchema, } from './field-applicability-by-type.js';
export const ENDPOINTS = {
    /* --- search ------------------------------------------------------------ */
    search: {
        method: 'POST',
        path: '/v1/search',
        request: SearchRequestSchema,
        response: SearchResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-18', 'FR-20', 'FR-25', 'FR-27', 'NFR-06', 'NFR-10'],
        notes: 'POST, though §2.4.4 draws it as GET. Three reasons: the narrowing filters are ' +
            'nested and do not survive query-string encoding legibly; the call writes an ' +
            'audit read event and a search-log row, so it is not the cacheable idempotent ' +
            'GET the diagram implies; and a query string would put contract search terms ' +
            'into proxy and CDN access logs, which is a needless second copy of who searched ' +
            'for what. Flagged for ratification — the architecture diagram says GET.',
    },
    'record-search-selection': {
        method: 'POST',
        path: '/v1/search/selection',
        request: SearchSelectionRequestSchema,
        response: SearchSelectionResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-26'],
        notes: 'Implicit relevance signal — which result the user actually opened (WP-09 §5 ' +
            'stage ⑥). Feeds the weakness dashboard: "shown but never selected, repeatedly" ' +
            'is how a clause ranked too high gets found. No audit row: this is search ' +
            'telemetry, not access to contract data, and it changes no learned behaviour on ' +
            'its own.',
    },
    /* --- retrieve-contract ------------------------------------------------- */
    'retrieve-contract': {
        method: 'GET',
        path: '/v1/contracts/{contract_key}',
        request: RetrieveContractRequestSchema,
        response: RetrieveContractResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-18', 'FR-01', 'FR-02', 'FR-03', 'FR-05', 'FR-09', 'FR-11', 'NFR-06'],
        notes: '`contract_key` is the path parameter; `include` is a repeated query parameter. ' +
            'An out-of-scope key returns 404, never 403 — see errors.ts.',
    },
    /* --- upload ------------------------------------------------------------ */
    'upload-options': {
        method: 'GET',
        path: '/v1/contracts/upload-options',
        request: UploadOptionsRequestSchema,
        response: UploadOptionsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-13', 'FR-14'],
        notes: 'Step 1 of the guided flow (§2.4.2). All fields are query parameters. This is ' +
            'what makes FR-14 achievable: the user selects from what the server offers ' +
            'rather than typing a key. CLM-118 (D-110-3): no type is offered or taken — ' +
            'the response carries no contract_types (B9). client_file_name, the selected ' +
            'file’s own name, is read for name_type (what the file-name rule gives: evidence, ' +
            'type, a SOW-shaped number, an amendment word) and narrows each candidate’s ' +
            'valid_relationships by the pair rules (B16); it is evidence only, never a path ' +
            'and never stored.',
    },
    upload: {
        method: 'POST',
        path: '/v1/contracts',
        request: UploadRequestSchema,
        response: UploadResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-13', 'FR-14', 'IR-05'],
        notes: 'multipart/form-data with two parts: `metadata` (application/json, validated ' +
            'against the request schema) and `document` (application/pdf, the bytes). ' +
            'Returns 202 — the row exists, the facts do not yet. CLM-118 (D-110-3): the ' +
            'request carries no type (a contract_type is a 400 from the strict schema). The ' +
            'server types the contract from the document’s own file name (a type word, or a ' +
            'SOW-shaped number), from the agreement an amends or supersedes follows, or from a ' +
            'typed SOW number (never trusted); otherwise it is Type pending (OTHER + pending). ' +
            'It records the evidence and any amendment word. 400 sow_number_conflicts_with_name ' +
            'refuses a typed number beside a name that says MSA or NDA or names two types.',
    },
    /* --- manage-clause ----------------------------------------------------- */
    'manage-clause': {
        method: 'POST',
        path: '/v1/clauses/actions',
        request: ManageClauseRequestSchema,
        response: ManageClauseResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'FR-17', 'FR-26', 'FR-28', 'NFR-13'],
        notes: 'A single action-union endpoint so the FR-17 reviewer role check happens in one ' +
            'place. Restricted to Legal/reviewer and AIMS roles (WP-09 §9).',
    },
    /* --- resolve-party ----------------------------------------------------- */
    'resolve-party': {
        method: 'GET',
        path: '/v1/parties/resolve',
        request: ResolvePartyRequestSchema,
        response: ResolvePartyResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-09', 'FR-10', 'FR-14', 'FR-29', 'IR-02', 'IR-03'],
        notes: 'All query parameters. Read-only — the pipeline AuditWriter log line only, no ' +
            'clm.audit_log row, because nothing is written. The write side is manage-party-alias ' +
            '(D-B8). The party list is corpus reference data (DR-04) and is not scoped; ' +
            'in_scope_contract_keys is, anchored on contract.root_contract_key, and ' +
            'page.total_in_scope is therefore the honest total of the filtered corpus.',
    },
    /* --- verify-field ------------------------------------------------------ */
    'verification-queue': {
        method: 'GET',
        path: '/v1/verifications',
        request: VerificationQueueRequestSchema,
        response: VerificationQueueResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07'],
        notes: 'The read side of the FR-07 queue. All query parameters.',
    },
    'verify-field': {
        method: 'POST',
        path: '/v1/verifications/{verification_id}/verify',
        request: VerifyFieldRequestSchema,
        response: VerifyFieldResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07', 'CR-01', 'NFR-07', 'NFR-12'],
        notes: '`verification_id` appears in both the path and the body and must match. This ' +
            'call creates the human half of the CR-01 evidence chain.',
    },
    /* --- acknowledge-notification ------------------------------------------ */
    notifications: {
        method: 'GET',
        path: '/v1/notifications',
        request: NotificationListRequestSchema,
        response: NotificationListResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['O-1', 'FR-01', 'FR-04'],
        notes: 'The read side of the §3 ladder. All query parameters.',
    },
    'acknowledge-notification': {
        method: 'POST',
        path: '/v1/notifications/acknowledge',
        request: AcknowledgeNotificationRequestSchema,
        response: AcknowledgeNotificationResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['O-1', 'FR-04', 'NFR-07'],
        notes: 'Addressed by the natural key (rule, contract, occurrence_date, tier), not by a ' +
            'surrogate id, so the acknowledgement is idempotent (§3.3).',
    },
    /* --- plan-sharepoint-ingest (CLM-43) ----------------------------------- */
    'plan-sharepoint-ingest': {
        method: 'POST',
        path: '/v1/admin/ingest/sharepoint/plan',
        request: PlanSharepointIngestRequestSchema,
        response: PlanSharepointIngestResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['IR-05', 'IR-01', 'FR-13', 'FR-14', 'NFR-06', 'NFR-07', 'NFR-09'],
        notes: 'The eighth endpoint, and the first one gated on a bulk-privilege action — ' +
            '`contract:bulk_ingest`, held by `contributor` and (by inheritance) `admin` ' +
            'since R-94-1 (CLM-94) widened it off Admin-only (CLM-43). JSON body. ' +
            'POST rather than GET for the reasons `search` gives: it writes an audit row, ' +
            'and a sharing link in a query string would put the tenant folder structure ' +
            'into proxy and CDN access logs. Returns 200 with a dry-run plan and changes ' +
            'nothing — `dry_run` is `z.literal(true)`, so "plan and also execute" is not a ' +
            'representable response. All SharePoint access is server-side (rule 11): the ' +
            'SPA sends a pasted URL and never speaks to Graph. An approved plan is ' +
            'executed by `execute-sharepoint-ingest`; the planner surface ticks and ' +
            'executes (CLM-67). CLM-118 (D-110-3): no type list in the response (B9); each ' +
            'planned row carries contract_type_name_evidence and amendment_signal_name, and a ' +
            'row whose name names two types or a type word beside a SOW-shaped number is ' +
            'planned with the planner’s own type, contract_type_confidence conflicting. ' +
            'CLM-122 (D-110-4 point 4): a row whose name gives no type, or contradicts itself, is planned as ' +
            'Type pending (`type_pending_reason`) and is never pre-ticked; totals count each skip and Type pending ' +
            'reason and the extraction calls the Type pending rows would cost.',
    },
    /* --- execute-sharepoint-ingest (CLM-50) -------------------------------- */
    'execute-sharepoint-ingest': {
        method: 'POST',
        path: '/v1/admin/ingest/sharepoint/execute',
        request: ExecuteSharepointIngestRequestSchema,
        response: ExecuteSharepointIngestResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-13', 'IR-05', 'NFR-07', 'NFR-08', 'CR-01'],
        notes: 'The ninth endpoint and the execute half of the pair the eighth opened; since ' +
            'CLM-67 the planner surface ticks rows and posts here. Same ' +
            '`contract:bulk_ingest` gate as the plan (contributor and admin, since ' +
            'R-94-1 / CLM-94), JSON body. The request carries ' +
            '`(drive_id, item_id, etag)` triples and nothing else: the server re-crawls the ' +
            'folder, re-runs the planner, and executes only the triples the fresh plan still ' +
            'wants — so a client-supplied party_key or sow_number is unrepresentable rather ' +
            'than merely rejected (rule 9), and a document re-signed since the plan is ' +
            'skipped rather than written under a stale eTag. Per document: download the ' +
            'bytes to verify them, then one transaction writing clm.contract, ' +
            'clm.contract_party and clm.sharepoint_ingest_ledger. Bytes are never ' +
            'persisted, logged or forwarded (IR-05). Returns 202 — `upload`’s wording, and ' +
            'for the same reason: the rows exist, the facts do not. Nothing here converts, ' +
            'pseudonymises or extracts; intake stops at PendingExtraction, or ' +
            'manual_entry_pending for a processing_restricted party (§4C, rule 4). ' +
            'CLM-118 (R-118-18): no contract_type_override — the reviewer type override and ' +
            'the offered-type gate are retired (D-76-4, D-76-5, D-76-7); a receipt’s ' +
            'contract_type_source is always derived and it carries contract_type_name_evidence. ' +
            'CLM-122: a Type pending row is filed `OTHER`, `pending`, and the receipt says so.',
    },
    /* --- skip / reopen a planned document (CLM-115) ------------------------- */
    'skip-sharepoint-document': {
        method: 'POST',
        path: '/v1/admin/ingest/sharepoint/skip',
        request: SkipSharepointDocumentRequestSchema,
        response: SkipSharepointDocumentResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['IR-05', 'FR-13', 'NFR-06', 'NFR-07'],
        notes: 'Records a reviewer’s decision not to ingest one document, so the planner ' +
            'stops re-listing it on every crawl — the plan is recomputed from scratch ' +
            'each time and the browser keeps nothing between tabs (D-67-6), so the ' +
            'decision has nowhere to live but the server. Same `contract:bulk_ingest` ' +
            'gate as the plan and execute it sits beside. Addresses the document by ' +
            '`(drive_id, item_id)` and never by eTag: the eTag is the version, and a ' +
            'version-addressed skip would be undone by an upload nobody reviewed. The ' +
            'eTag the reviewer saw rides along as `skipped_etag` so the plan can mark a ' +
            'document changed since the decision (D-115-1) without returning it to the ' +
            'queue. Org-wide by D-115-2 — the folder is a shared backfill queue. A ' +
            'second skip of an already-skipped document is the 23505 on ' +
            '`ux_sharepoint_ingest_skip_open`, mapped to 409, never a read-then-write ' +
            'check. Writes nothing to `sharepoint_ingest_ledger`: a ledger row means ' +
            'ingested, and 0061’s UNIQUE would then refuse the real ingest later. ' +
            'CLM-122: a Type pending row may be skipped; its reason is recorded as `planner_reason`.',
    },
    'reopen-sharepoint-document': {
        method: 'POST',
        path: '/v1/admin/ingest/sharepoint/reopen',
        request: ReopenSharepointDocumentRequestSchema,
        response: ReopenSharepointDocumentResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['IR-05', 'FR-13', 'NFR-06', 'NFR-07'],
        notes: 'Puts a skipped document back in front of the next plan. An UPDATE setting ' +
            '`reopened_utc`, never a DELETE: the decision and its withdrawal are both ' +
            'governance acts and the row is the evidence of them (rule 7), so the ' +
            'trigger writes Update rather than the history disappearing. The partial ' +
            'UNIQUE is what frees the document to be skipped again afterwards. ' +
            'Reopening something already reopened — or never skipped — is a 404 ' +
            'rather than a silent success, because the caller is acting on a row it ' +
            'believes exists.',
    },
    /* --- clause-candidates (CLM-36) ---------------------------------------- */
    'clause-candidates': {
        method: 'GET',
        path: '/v1/clause-candidates',
        request: ClauseCandidateListRequestSchema,
        response: ClauseCandidateListResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'FR-17', 'NFR-06', 'NFR-07'],
        notes: 'The tenth endpoint, and the Legal review queue for architecture.md §6 clause ' +
            'library mining. All query parameters. Ranked by frequency — member count, then ' +
            'distinct contract count, then cluster id — which is the order the partial index ' +
            'ix_clause_cluster_pending (migration 0081) is built in, so the queue and the ' +
            'operator CLI report agree about what "top 50" means. Writes an audit read event ' +
            'like every other read (NFR-07). The cluster tables are corpus-level and carry no ' +
            'party anchor, so this list is unscoped and FR-17 is why: all authorised may read ' +
            'the library. The clusters MEMBERS are scoped, which is the drill-down.',
    },
    'clause-candidate-detail': {
        method: 'GET',
        path: '/v1/clause-candidates/{cluster_id}',
        request: ClauseCandidateDetailRequestSchema,
        response: ClauseCandidateDetailResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'FR-17', 'NFR-06', 'NFR-07'],
        notes: 'The drill-down: which paragraphs are in this cluster, as SPANS into contracts the ' +
            'caller may read (rule 13 — the paragraph text is stored once on clm.contract and ' +
            'the browser follows the span). The variant list carries the scope predicate ' +
            'anchored on root_contract_key, so two reviewers with different practice scopes see ' +
            'different counts for one cluster — which is why the field is named ' +
            'variants_in_scope rather than variants, with the corpus-wide member_count beside ' +
            'it. A count discloses no party; a variant does.',
    },
    'clause-candidate-decision': {
        method: 'POST',
        path: '/v1/clause-candidates/{cluster_id}/decision',
        request: ClauseCandidateDecisionRequestSchema,
        response: ClauseCandidateDecisionResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'FR-17', 'CR-01', 'NFR-07'],
        notes: 'The twelfth endpoint and the only one in this pair that writes. Two gates, like ' +
            'manage-clause and for the same reason: the union is not equally privileged. The ' +
            'coarse gate is clause:review_match; accept then needs clause:edit_master_text ' +
            '(legal_reviewer only, FR-17) because it is the only decision that publishes a ' +
            'clause_library row and the only member that can carry master_text. ' +
            'reject/merge/split never write to clause_library. The reviewer supplies both ' +
            'master_text and clause_code — the model proposes a description, never the clause ' +
            'wording, and never the natural key every later revision hangs off. The ' +
            'clause_library insert, the cluster update and the audit row are one committing ' +
            'transaction. A stale seen_review_state is 409, not a silent overwrite. The ' +
            'published clause has no vectors until `embed-clauses` next runs — deliberate: a ' +
            'second vector write path here would break "no second master" for convenience.',
    },
    /* --- upload-bundle (CLM-57) -------------------------------------------- */
    'upload-bundle-plan': {
        method: 'POST',
        path: '/v1/contracts/uploads/plan',
        request: UploadBundlePlanRequestSchema,
        response: UploadBundlePlanResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-13', 'FR-14', 'IR-01'],
        notes: 'multipart/form-data with the two server-named parts `metadata` (application/json, ' +
            'validated against the request schema) and `document` (one PDF or one ZIP of PDFs). ' +
            'Expands the archive, hashes and detects each entry, flags duplicates within the bundle ' +
            'and against what is already filed; writes nothing and persists no plan (rule 8). ' +
            'Returns 200 — the review table, with parties and parents the caller may choose from. ' +
            'CLM-118 (D-110-3): no default type and no type list; each row is typed by the ' +
            'server folder first (CLM-57) from a type word or a SOW-shaped number, with its ' +
            'name_evidence and amendment_signal; an untyped or conflicting row is Type pending ' +
            '(needs_type / type_evidence_conflicting, never blocking).',
    },
    'upload-bundle-execute': {
        method: 'POST',
        path: '/v1/contracts/uploads/execute',
        request: UploadBundleExecuteRequestSchema,
        response: UploadBundleExecuteResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-13', 'FR-14', 'IR-01', 'IR-05'],
        notes: 'multipart/form-data with the same two parts: the bundle is re-sent and re-expanded, and ' +
            'bundle_sha256 plus every approved row sha256 is re-verified (409 if anything changed). ' +
            'Each approved row then runs the guided upload executor — one contract row, one Create ' +
            'audit row, one ledger row, per-document transaction. Returns 200 with a per-row result ' +
            'array: a per-row 202 cannot be expressed on a batch, so the row body carries the ' +
            'contract\'s own UploadResponse instead. CLM-118 (D-110-3): no row type; the ' +
            'server re-detects each row’s evidence from its path against the same selectable ' +
            'parties the plan read, and a SOW row with no number is refused per row ' +
            '(sow_number_required).',
    },
    /* --- clauses (CLM-55) -------------------------------------------------- */
    clauses: {
        method: 'GET',
        path: '/v1/clauses',
        request: ClauseListRequestSchema,
        response: ClauseListResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'FR-17'],
        notes: 'All query parameters; corpus-level list, FR-17 read grant; 200. The read side of ' +
            'the clause library: clause search needs a `q` and cannot list. clause_library has ' +
            'no party or contract column, so like clause-candidates the list is unscoped and ' +
            'total_in_scope is null. Keyset paging on clause_code. Writes an audit read event ' +
            'like every other read (NFR-07).',
    },
    'clause-detail': {
        method: 'GET',
        path: '/v1/clauses/{clause_code}',
        request: ClauseDetailRequestSchema,
        response: ClauseDetailResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'FR-17'],
        notes: 'Path parameter; usage count scoped on root_contract_key; 200 / 404. The current ' +
            'clause, its whole version chain newest first (0021 one row per version, 0083 ' +
            'revision_note), each version’s embedding_state, and in_scope_usage_count — the ' +
            'one scoped figure, a count of contracts the caller may read that reference the ' +
            'clause. A retired clause is still current and still found; only an unknown code ' +
            'is 404.',
    },
    /* --- manage-party-alias (CLM-56, D-B8) --------------------------------- */
    'manage-party-alias': {
        method: 'POST',
        path: '/v1/parties/aliases/actions',
        request: ManagePartyAliasRequestSchema,
        response: ManagePartyAliasResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-29', 'NFR-13', 'NFR-07'],
        notes: 'JSON body; the body is the action (discriminated on `action`), no envelope — the ' +
            'manage-clause shape. One write per call, one transaction, one clm.audit_log row written ' +
            'by tg_party_alias_audit (0046). Addressed by the natural key (party_key, alias_text), ' +
            'case-insensitive. Success 200. D-B8.',
    },
    /* --- quality dashboards (CLM-40, D-B11) --------------------------------- */
    'quality-search-weakness': {
        method: 'GET',
        path: '/v1/quality/search-weakness',
        request: SearchWeaknessRequestSchema,
        response: SearchWeaknessResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-26', 'O-6', 'FR-27'],
        notes: 'All query parameters; gate clause:review_match (admin ∪ Legal — the log carries other ' +
            'users’ query text); corpus-level (clause_search_log has no contract column), so ' +
            'total_in_scope is null. Totals for the window plus one keyset-paged list per signal ' +
            '(zero_result | unselected | drift | not_relevant_top). Read-only, no audit row. 200 / 400 / 403.',
    },
    'quality-outbound-health': {
        method: 'GET',
        path: '/v1/quality/outbound-health',
        request: OutboundHealthRequestSchema,
        response: OutboundHealthResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['NFR-11', 'NFR-15'],
        notes: 'All query parameters; gate clause:review_match. cells are system-level per the ratified ' +
            'vw_outbound_health (0048); days and items read vw_outbound_queue_scope under the scope ' +
            'predicate on effective_contract_key (G20). Never reads outbound_queue directly. An empty ' +
            'queue is a 200 with empty arrays. Read-only, no audit row. 200 / 400 / 403.',
    },
    'quality-obligation-owners': {
        method: 'GET',
        path: '/v1/quality/obligation-owners',
        request: ObligationOwnersRequestSchema,
        response: ObligationOwnersResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['O-1', 'NFR-06'],
        notes: 'All query parameters; gate contract:read — every role, **scoped** on root_contract_key for ' +
            'totals and rows alike (an amendment inherits its parent’s visibility). role_mappings is the ' +
            'three-row status of practice_director / pmo / elt under the exact predicate notify resolves ' +
            'recipients with. Read-only, no audit row. 200 / 400.',
    },
    'quality-stale-judgements': {
        method: 'GET',
        path: '/v1/quality/stale-judgements',
        request: StaleJudgementsRequestSchema,
        response: StaleJudgementsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-26', 'NFR-13'],
        notes: 'All query parameters; gate clause:review_match; corpus-level (total_in_scope null). ' +
            'vw_clause_search_judgement WHERE is_stale, oldest first — the re-confirmation queue. Zero ' +
            'feedback rows is a 200 with zeros. Read-only, no audit row. 200 / 400 / 403.',
    },
    /* --- search quality reads (CLM-39, D-B10) ------------------------------ */
    'search-judgements': {
        method: 'GET',
        path: '/v1/search/judgements',
        request: SearchJudgementListRequestSchema,
        response: SearchJudgementListResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-26', 'NFR-13'],
        notes: 'All query parameters; corpus-level ledger of active search judgements over ' +
            'vw_clause_search_judgement (0026) joined to clause_search_feedback for embedding_model ' +
            '(0085); stale first, keyset on (is_stale, feedback_id); stale_only defaults to false — the ' +
            're-confirmation queue itself is quality-stale-judgements (CLM-40). Gated clause:review_match ' +
            '(admin and Legal). Writes no audit row of its own: a read over search-tuning rows, not contract data.',
    },
    'search-boosts': {
        method: 'GET',
        path: '/v1/search/boosts',
        request: SearchBoostListRequestSchema,
        response: SearchBoostListResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-26', 'NFR-13'],
        notes: 'All query parameters; current clauses whose boost_weight is non-zero, with the boost_reason ' +
            '0085 records (D-B10); keyset on clause_code; totals pinned (> 0) and buried (< 0). Gated ' +
            'clause:review_match. Corpus-level (FR-17), no audit row of its own.',
    },
    'search-stability': {
        method: 'POST',
        path: '/v1/search/stability',
        request: SearchStabilityRequestSchema,
        response: SearchStabilityResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['O-6', 'FR-27'],
        notes: 'JSON body — POST for the same reason search is: a query string would put search terms in ' +
            'access logs. Lists the last N logged runs of one query text (clause_search_log) newest first ' +
            'and compares adjacent pairs: drift when top_clause_code or round(top_score, 4) differs; ' +
            'tuned_since when a feedback, library or alias row changed between the two runs, derived from ' +
            'the source tables, never from audit_log. A hint, not a proof. No re-ranking is performed. ' +
            'Gated clause:review_match. Corpus-level, no audit row of its own.',
    },
    /* --- search-aliases (CLM-59, FR-26) ------------------------------------ */
    'search-aliases': {
        method: 'GET',
        path: '/v1/search/aliases',
        request: SearchAliasListRequestSchema,
        response: SearchAliasListResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-26'],
        notes: 'All query parameters; 200. The curated query vocabulary with a derived status ' +
            '(suggested / active / inactive) and unfiltered totals; status=suggested is the ' +
            'suggested_aliases review queue. Unscoped: clause_search_alias carries no party or ' +
            'contract column and FR-17 grants the library to every authorised user, so ' +
            'total_in_scope is null. Keyset paging on alias_id. No domain audit row — nothing is ' +
            'written; the read event is the standard one (NFR-07).',
    },
    'search-aliases-mine': {
        method: 'POST',
        path: '/v1/search/aliases/mine',
        request: SearchAliasMineRequestSchema,
        response: SearchAliasMineResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-26', 'NFR-13'],
        notes: 'Empty JSON body; 200. Proposes origin=Mined, is_active=false aliases from repeated ' +
            'zero-result queries in clause_search_log by lexical similarity to the active vocabulary ' +
            'and current clause titles — the system proposes, a Legal reviewer approves through ' +
            'manage-clause. Idempotent on lower(btrim(alias_term)); a term in any status is skipped. ' +
            'One transaction, one tg_clause_search_alias_audit row per proposal (NFR-13), each ' +
            'returned as a receipt. Unscoped for the same FR-17 reason as the list. Legal-only ' +
            '(search:tune): mining writes proposals.',
    },
    /* --- reconcile-corpus (CLM-35, D-B17) ---------------------------------- */
    'reconcile-corpus': {
        method: 'POST',
        path: '/v1/admin/corpus/reconcile',
        request: ReconcileCorpusRequestSchema,
        response: ReconcileCorpusResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-15', 'NFR-09', 'NFR-06', 'NFR-07', 'IR-01'],
        notes: 'FR-15: the stored corpus compared against the authoritative SOW list (Qfly ' +
            'opportunities carrying a SOW number — Q3 / A4). On the existing ' +
            '`contract:bulk_ingest` action (contributor and admin, since R-94-1 / CLM-94), ' +
            'JSON body, for the reasons plan-sharepoint-ingest ' +
            'gives: the person who reads the exception list is the person who remediates it, and ' +
            'a narrower read-only action would be a second privilege for one report. POST because ' +
            'it writes one clm.audit_log row (`Read`, source_component corpus-reconciliation, ' +
            'totals + list digest) and the list is a body, not a query string. **It changes no ' +
            'contract row** — the response *is* the artefact. Every corpus read is scoped on ' +
            'root_contract_key; a practice-scoped admin gets `scope_complete: false`, never a ' +
            'silently partial "no gaps". No LLM, no embedding, no register table (K5). D-B17.',
    },
    /* --- clause-matches (CLM-34) ------------------------------------------- */
    'clause-matches': {
        method: 'GET',
        path: '/v1/clause-matches',
        request: ClauseMatchListRequestSchema,
        response: ClauseMatchListResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-28', 'NFR-06', 'CR-01'],
        notes: 'All query parameters; the clause matcher\'s review queue (§4.2). queue is one of ' +
            'pending_match_review (MEDIUM band) or bespoke_candidates (LOW band, no clause, not ' +
            'restricted, not a no-library-vectors row); the two Phase-5 queues are 400 here. Rows are ' +
            'paragraphs of contracts, so the list is SCOPED on root_contract_key and both totals and ' +
            'total_in_scope are real scoped figures; an out-of-scope contract_key filter is an empty ' +
            'page, not 403. Keyset paging on contract_clause_id. No model and no embedding call ' +
            '(rule 14). Writes an audit read event like every other read (NFR-07). Gate ' +
            'clause:review_match (admin + legal_reviewer).',
    },
    /* --- pipeline-status (CLM-70) ------------------------------------------ */
    'pipeline-status': {
        method: 'GET',
        path: '/v1/admin/pipeline/status',
        request: PipelineStatusRequestSchema,
        response: PipelineStatusResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['NFR-07', 'CR-01', 'NFR-06', 'FR-07'],
        notes: 'All query parameters; gate contract:read. Counts, lists and the optional timeline are ' +
            'SCOPED on root_contract_key; reasons are the audit rows’ text verbatim; ' +
            'Pseudonymised/Verified times come from 0046 rows (no column). No LIMIT (rule 17). ' +
            'Read-only, no audit row. 200 / 400 / 403.',
    },
    /* --- contract tags (CLM-77) -------------------------------------------- */
    'manage-contract-tags': {
        method: 'POST',
        path: '/v1/contracts/{contract_key}/tags',
        request: ManageContractTagsRequestSchema,
        response: ManageContractTagsResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-02', 'NFR-06', 'NFR-07'],
        notes: 'Body is the replacement set (D-77-5, last write wins); gate contract:update (contributor+). ' +
            'One attributed transaction — DELETE the tags not in the set, INSERT the missing ones, and ' +
            'exactly ONE Update audit row (field_name tags, old and new sets as objects); contract_tag ' +
            'carries no trigger (D-77-4, rule 7). A no-op set still writes the row. Every tag must arrive ' +
            'normalised (D-77-1: lowercase, single spaces, [a-z0-9 -], 1..40, at most 20) — 400 ' +
            'validation_failed with path tags[i] otherwise. SCOPED on root_contract_key: an out-of-scope ' +
            'or absent contract is 404 before any write (retrieve-contract\'s behaviour). 200 / 400 / 403 / 404.',
    },
    'contract-tags-suggest': {
        method: 'GET',
        path: '/v1/contract-tags',
        request: ContractTagSuggestRequestSchema,
        response: ContractTagSuggestResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-02', 'NFR-06'],
        notes: 'All query parameters; gate contract:read (readers need the list to filter — D-77-2). The ' +
            'tags already on contracts in the caller\'s scope (root_contract_key) that start with q ' +
            '(normalised server-side, starts_with — no LIKE escaping), most-used first then alphabetical, ' +
            'limit 1..50 (default 10). Never learns another scope\'s vocabulary. Read-only, no audit row. ' +
            '200 / 400 / 403.',
    },
    /* --- pipeline re-run (CLM-79) ------------------------------------------ */
    'rerun-pipeline-contract': {
        method: 'POST',
        path: '/v1/contracts/{contract_key}/pipeline/rerun',
        request: RerunPipelineContractRequestSchema,
        response: RerunPipelineContractResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07', 'NFR-06', 'NFR-07'],
        notes: 'Path contract_key, JSON body { reason? }; gate contract:update (contributor+, D-79-7). The row ' +
            'must be ExtractionFailed with NO AI audit row (it failed before the boundary — conversion gate ' +
            'or pseudonymisation) and carry an ingest-ledger row; otherwise 409 conflict with the reason in ' +
            'field_errors (retryable_pending / failed_after_ai / not_failed / no_ledger_row) and nothing ' +
            'written. ONE scoped UPDATE resets the contract to PendingExtraction (manual_entry_pending when ' +
            'restricted), data_trust PendingExtraction, and clears full_text, page_count, conversion_method, ' +
            'converted_utc, pseudonym_map_version so the next run-pipeline dispatch converts it again. The ' +
            'single Update audit row is written by tg_contract_audit (0046) with source_component ' +
            'pipeline-rerun — not by this endpoint (D-79-1); the response carries it read back in the same ' +
            'transaction. SCOPED on root_contract_key: out of scope or absent is 404. reason is validated ' +
            '(1..1000) and logged, not stored (D-79-6). Does not start a run. 200 / 400 / 403 / 404 / 409.',
    },
    /* --- register-vendor-party (CLM-78) ------------------------------------ */
    'register-vendor-party': {
        method: 'POST',
        path: '/v1/parties/register-vendor',
        request: RegisterVendorPartyRequestSchema,
        response: RegisterVendorPartyResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-14', 'FR-29', 'IR-03', 'CR-01'],
        notes: 'Body is the typed legal name and nothing else — the key is minted server-side from the ' +
            'reserved V… space (FR-14). Gate party:register_vendor (contributor+, A1). One attributed ' +
            'transaction: duplicate check by normalised name AND active alias (409 vendor_exists with ' +
            'the existing party), key scan, INSERT party (sor_system CLM, sor_reference_id = party_key, ' +
            'registered_by_upn — migration 0090), INSERT the typed name as a confirmed Manual alias. ' +
            'Two writes, two trigger audit rows (rule 7); the receipt is the alias row\'s. ' +
            '201 / 400 / 403 / 409 (vendor_exists | vendor_key_space_exhausted).',
    },
    /* --- analytics (CLM-81) ------------------------------------------------- */
    'analytics-overview': {
        method: 'GET',
        path: '/v1/analytics/overview',
        request: AnalyticsOverviewRequestSchema,
        response: AnalyticsOverviewResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-07', 'NFR-06', 'NFR-10', 'O-1'],
        notes: 'All query parameters; gate contract:read (reader+). Every count is one scoped SELECT ' +
            'anchored on root_contract_key. period narrows by created_utc (server interval map); ' +
            'party_key is the primary party and narrows beside the scope, never instead of it — out ' +
            'of scope ⇒ zeros, not 403. Eight processing_status tiles, five contract_type tiles, ' +
            'today’s intake bounded by day_start_utc (D-81-9); clients[] is the scoped selector list ' +
            '(D-81-8). No money figures: contract value is an extracted string (D-81-2). No LLM, no ' +
            'cache (K5, NFR-10). Read-only, no audit row. 200 / 400 / 403.',
    },
    'analytics-dashboard': {
        method: 'GET',
        path: '/v1/analytics/dashboard',
        request: AnalyticsDashboardRequestSchema,
        response: AnalyticsDashboardResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-07', 'O-1', 'NFR-06', 'NFR-10', 'CR-01'],
        notes: 'party_key only; gate contract:read (reader+). Every count is one scoped SELECT anchored ' +
            'on root_contract_key — obligations, alerts and field rows are reached only through JOIN ' +
            'contract. party_key narrows beside the scope, never instead of it — out of scope ⇒ zeros, ' +
            "not 403. Time to verify = created_utc → the 0046 row carrying data_trust = 'Verified' " +
            '(D-81-6); reviewer rows come from field_verification (CR-01). No money figures (D-81-2). ' +
            'No LLM, no cache (K5, NFR-10). Read-only, no audit row. 200 / 400 / 403. attention: due ' +
            'windows on COALESCE(renewal_notice_date, expiry_date) and per-tier ledger status (CLM-90, D-90-10).',
    },
    /* --- pipeline runs (CLM-83) --------------------------------------------- */
    'request-pipeline-run': {
        method: 'POST',
        path: '/v1/pipeline/runs',
        request: RequestPipelineRunRequestSchema,
        response: RequestPipelineRunResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07', 'NFR-06', 'NFR-07', 'CR-01'],
        notes: 'JSON body { filter?, reason }; gate pipeline:request (contributor+). One INSERT status=requested, ' +
            'source=manual, requested_by_upn=actor; tg_pipeline_run_audit (0092) writes the row. Org-level: no ' +
            'scope predicate (D-83-14). 409 conflict run_already_pending when ux_pipeline_run_one_active ' +
            'refuses a second requested/approved/running row (D-83-5, no pre-check SELECT). filter.contract_type ' +
            'and filter.tags are recorded but not applied by the worker today. 201 / 400 / 403 / 409.',
    },
    'approve-pipeline-run': {
        method: 'POST',
        path: '/v1/pipeline/runs/{pipeline_run_id}/approve',
        request: RunIdRequestSchema,
        response: ApprovePipelineRunResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07', 'NFR-06', 'NFR-07', 'CR-01'],
        notes: 'Path pipeline_run_id, empty body; gate pipeline:approve (ADMIN ONLY — D-83-2). requested → approved, ' +
            'approved_by_upn=actor, approved_utc=now(); the worker claims it within one poll. Self-approval by an ' +
            'admin is allowed (two audit rows). 409 conflict when the row is not requested; 404 when absent. ' +
            '200 / 400 / 403 / 404 / 409.',
    },
    'run-pipeline-now': {
        method: 'POST',
        path: '/v1/pipeline/runs/now',
        request: RunPipelineNowRequestSchema,
        response: RunPipelineNowResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07', 'NFR-06', 'NFR-07', 'CR-01'],
        notes: 'Same body as request-pipeline-run; gate pipeline:approve (ADMIN ONLY — D-83-3). ONE INSERT with ' +
            'status=approved, requested_by_upn=approved_by_upn=actor, requested_utc=approved_utc — create and ' +
            'approve in one action, so the worker picks it up within its poll interval. Same 409 ' +
            'run_already_pending as request. 201 / 400 / 403 / 409.',
    },
    'cancel-pipeline-run': {
        method: 'POST',
        path: '/v1/pipeline/runs/{pipeline_run_id}/cancel',
        request: RunIdRequestSchema,
        response: CancelPipelineRunResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07', 'NFR-06', 'NFR-07'],
        notes: 'Path pipeline_run_id, empty body; gate pipeline:request. The requester may cancel their own ' +
            'requested row; an admin (pipeline:approve) may cancel any requested/approved/running row — a running ' +
            'run stops cooperatively after the batch and keeps status=cancelled with counts filled (D-83-7). ' +
            '403 when another contributor\'s row; 409 conflict when terminal; 404 when absent. 200 / 403 / 404 / 409.',
    },
    'list-pipeline-runs': {
        method: 'GET',
        path: '/v1/pipeline/runs',
        request: ListPipelineRunsRequestSchema,
        response: ListPipelineRunsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-07', 'NFR-06', 'NFR-07', 'CR-01'],
        notes: 'No parameters; gate contract:read (everyone — D-83-15). active = the one running row or null, ' +
            'pending[] = requested/approved, history[] = the last 20 terminal rows by finished_utc DESC. ' +
            'Read-only, no audit row. 200 / 403.',
    },
    'get-pipeline-run': {
        method: 'GET',
        path: '/v1/pipeline/runs/{pipeline_run_id}',
        request: RunIdRequestSchema,
        response: GetPipelineRunResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-07', 'NFR-06', 'CR-01'],
        notes: 'Path pipeline_run_id; gate contract:read (D-83-15). One row — polled every 5 s by the active card ' +
            'for heartbeat and counts. Read-only, no audit row. 200 / 400 / 403 / 404.',
    },
    'get-pipeline-schedule': {
        method: 'GET',
        path: '/v1/pipeline/schedule',
        request: GetPipelineScheduleRequestSchema,
        response: GetPipelineScheduleResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-07', 'NFR-06'],
        notes: 'No parameters; gate pipeline:approve (admin). The clm.pipeline_schedule singleton (0092), seeded ' +
            'disabled at 02:00 UTC, batch 50. Read-only, no audit row. 200 / 403.',
    },
    'set-pipeline-schedule': {
        method: 'POST',
        path: '/v1/pipeline/schedule',
        request: SetPipelineScheduleRequestSchema,
        response: SetPipelineScheduleResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07', 'NFR-06', 'NFR-07', 'CR-01'],
        notes: 'JSON body { enabled, hour_utc, batch_limit }; gate pipeline:approve (admin). One UPDATE of the ' +
            'singleton with updated_by_upn=actor, updated_utc=now(); tg_pipeline_schedule_audit writes the row. ' +
            'The saving admin is the recorded approver of every run the schedule produces (D-83-4). ' +
            '200 / 400 / 403.',
    },
    /* --- verification queue bulk actions (CLM-86) --------------------------- */
    'verify-fields': {
        method: 'POST',
        path: '/v1/verifications/bulk',
        request: VerifyFieldsBulkRequestSchema,
        response: VerifyFieldsBulkResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-07', 'CR-01', 'NFR-06', 'NFR-07'],
        notes: 'Mass Confirm / Reject over the Verification queue (R-86-1). Gate field:verify ' +
            '(contributor+) coarse; per item the refineVerifyFieldAction gate — a counterparty-name ' +
            'row the caller may not decide (party:manage_alias) is not_permitted, nothing written. ' +
            'Each item is its own transaction through the single-field settle (settleOneField): one ' +
            'HumanVerification row per settled item, no summary row (ck_audit_log_field_attributable). ' +
            'seen_extracted_value cross-checked per item — not_pending on a stale value or an ' +
            'already-settled row. SCOPED on root_contract_key; out of scope ⇒ not_found. ≤ 200 items; ' +
            'reason required for reject and applied to every row. Not atomic by design (D-86-7): the ' +
            'response says which rows landed. 200 (counts, even when mixed) / 400 / 403.',
    },
    /* --- region (CLM-87) ------------------------------------------------------ */
    'set-party-region': {
        method: 'POST',
        path: '/v1/parties/{party_key}/region',
        request: SetPartyRegionRequestSchema,
        response: SetPartyRegionResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-09', 'NFR-07', 'CR-01'],
        notes: 'Body { region | null, note? }; gate party:set_region (admin only — D-87-10). One UPDATE party ' +
            'SET region, region_source = admin (null clears both and re-arms the first-verified rule); ' +
            'tg_party_audit writes the row. 200 / 400 / 403 / 404.',
    },
    /* --- due for renewal (CLM-90) --------------------------------------------- */
    'analytics-renewals-due': {
        method: 'GET',
        path: '/v1/analytics/renewals-due',
        request: AnalyticsRenewalsDueRequestSchema,
        response: AnalyticsRenewalsDueResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['O-1', 'FR-04', 'FR-07', 'NFR-06', 'NFR-10'],
        notes: 'All query parameters; gate contract:read (reader+). One row per Executed/Active contract whose ' +
            'COALESCE(renewal_notice_date, expiry_date) < today + window (past-due included, window=all ⇒ no bound), ' +
            'SCOPED on root_contract_key; party_key / region narrow beside the scope (D-81-5 / D-87-11). Sorted ' +
            'days_left ASC, contract_key (O-6). Five tier rows per contract from the ledger keyed ' +
            '(rule_id, contract_key, occurrence_date = due_date, tier) (rule 15; D-90-10). Cursor = offset + ' +
            'filter fingerprint (notification/list.ts shape). Read-only, no audit row. 200 / 400 / 403.',
    },
    /* --- cloudEQ entity (CLM-91) --------------------------------------------- */
    'list-cloudeq-entities': {
        method: 'GET',
        path: '/v1/cloudeq-entities',
        request: ListCloudeqEntitiesRequestSchema,
        response: ListCloudeqEntitiesResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-27', 'NFR-07'],
        notes: 'The active clm.cloudeq_entity registry rows in sort_order (D-91-2) — the selects and the ' +
            'dashboard tiles read display names from here, never from a constant (the fifth entity is ' +
            'TBC, Q-91-1). A reference-table read: no contract_key, so no scope predicate; no audit row. ' +
            'Gate contract:read. 200 / 403.',
    },
    'set-contract-cloudeq-entity': {
        method: 'POST',
        path: '/v1/contracts/{contract_key}/cloudeq-entity',
        request: SetContractCloudeqEntityRequestSchema,
        response: SetContractCloudeqEntityResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-27', 'NFR-06', 'NFR-07', 'CR-01'],
        notes: 'Body { entity_key | null, note? }; gate contract:set_cloudeq_entity (admin only — D-91-4). One ' +
            'UPDATE contract SET cloudeq_entity_key, cloudeq_entity_source = admin, cloudeq_entity_scan_note = ' +
            'NULL, SCOPED on root_contract_key (out of scope ⇒ 404, never 403); null clears both and re-arms ' +
            'the scan. A later scan never overwrites an admin value. tg_contract_audit writes the row. ' +
            '200 / 400 / 403 / 404.',
    },
    'rescan-cloudeq-entity': {
        method: 'POST',
        path: '/v1/admin/cloudeq-entity/rescan',
        request: RescanCloudeqEntityRequestSchema,
        response: RescanCloudeqEntityResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-27', 'NFR-06', 'NFR-07', 'CR-01'],
        notes: 'Body { contract_keys? (≤ 200), limit? 1..500 (default 200), dry_run? }; gate ' +
            'contract:set_cloudeq_entity (admin only — D-91-7). Inline, one withActor transaction: registry ' +
            'read, SCOPED candidates (full_text IS NOT NULL, source IS DISTINCT FROM admin) FOR UPDATE, the ' +
            'pure D-91-8 matcher per row, one UPDATE per contract whose key or note changes ⇒ one ' +
            'tg_contract_audit row each, none for unchanged or admin rows. dry_run writes nothing. No model, ' +
            'no conversion, no download. 200 / 400 / 403.',
    },
    /* --- CLM-95 · the list behind a KPI / status-mix tile (MERGE-ORDER:
       appended at END, after cloudEQ entity / CLM-91 — not squeezed between
       analytics-renewals-due and the entity block, which cloudeq-entity.test.ts
       asserts are four consecutive entries) ----------------------------------- */
    'analytics-contracts-list': {
        method: 'GET',
        path: '/v1/analytics/contracts-list',
        request: AnalyticsContractsListRequestSchema,
        response: AnalyticsContractsListResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['O-1', 'NFR-06', 'NFR-10'],
        notes: 'All query parameters; gate contract:read (reader+). D-95-1 — not `search`: `search` requires a ' +
            'text query baked unconditionally into its SQL, so there is no path there to "everything in scope ' +
            'matching these filters" alone. lifecycle_status / processing_status / expiring_within_days narrow ' +
            'beside the scope (never instead of it), matching the exact predicates `analytics-dashboard`\'s KPIs ' +
            'already count with — expiring_within_days is exclusive (< current_date + days), the same bound ' +
            '`expiringWithin()` uses, so the list can never disagree with the tile\'s own count by an off-by-one ' +
            'day. Returns the same ContractSummary rows `search` hits embed, with no snippets, no reason trace, ' +
            'no search_id — a plain list, not a ranked retrieval. Cursor = offset + filter fingerprint ' +
            '(analytics-renewals-due shape). Read-only, no audit row. 200 / 400 / 403.',
    },
    /* --- clause mining dry runs (CLM-103, first slice) — dry-run only; the ---
       0092/CLM-83 shape, restated for mine-clauses' segment/cluster/describe
       stages. A live run is out of scope here and stays on mine-clauses.yml. --- */
    'request-clause-mining-dry-run': {
        method: 'POST',
        path: '/v1/clause-mining/dry-runs',
        request: RequestClauseMiningDryRunRequestSchema,
        response: RequestClauseMiningDryRunResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'NFR-06', 'NFR-07'],
        notes: 'JSON body { filter, reason }; gate clause_mining:request (contributor+). filter is a ' +
            'discriminated union on filter.stage (segment | cluster | describe), each carrying only that ' +
            'stage\'s own parameters. One INSERT status=requested, dry_run=true, requested_by_upn=actor; ' +
            'tg_clause_mining_dry_run_audit (0097) writes the row. Org-level: no scope predicate. 409 conflict ' +
            'dry_run_already_pending when ux_clause_mining_dry_run_one_active refuses a second ' +
            'requested/approved/running row (no pre-check SELECT). 201 / 400 / 403 / 409.',
    },
    'approve-clause-mining-dry-run': {
        method: 'POST',
        path: '/v1/clause-mining/dry-runs/{clause_mining_dry_run_id}/approve',
        request: ClauseMiningDryRunIdRequestSchema,
        response: ApproveClauseMiningDryRunResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'NFR-06', 'NFR-07'],
        notes: 'Path clause_mining_dry_run_id, empty body; gate clause_mining:approve (admin only). ' +
            'requested → approved, approved_by_upn=actor, approved_utc=now(); the worker claims it within one ' +
            'poll. Self-approval by an admin is allowed. 409 conflict when the row is not requested; 404 when ' +
            'absent. 200 / 400 / 403 / 404 / 409.',
    },
    'run-clause-mining-dry-run-now': {
        method: 'POST',
        path: '/v1/clause-mining/dry-runs/now',
        request: RunClauseMiningDryRunNowRequestSchema,
        response: RunClauseMiningDryRunNowResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'NFR-06', 'NFR-07'],
        notes: 'Same body as request-clause-mining-dry-run; gate clause_mining:approve (admin only). ONE INSERT ' +
            'with status=approved, requested_by_upn=approved_by_upn=actor — create and approve in one action. ' +
            'Same 409 dry_run_already_pending as request. 201 / 400 / 403 / 409.',
    },
    'cancel-clause-mining-dry-run': {
        method: 'POST',
        path: '/v1/clause-mining/dry-runs/{clause_mining_dry_run_id}/cancel',
        request: ClauseMiningDryRunIdRequestSchema,
        response: CancelClauseMiningDryRunResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-16', 'NFR-06', 'NFR-07'],
        notes: 'Path clause_mining_dry_run_id, empty body; gate clause_mining:request. The requester may cancel ' +
            'their own requested row; an admin (clause_mining:approve) may cancel any requested/approved/running ' +
            'row. 403 when another contributor\'s row; 409 conflict when terminal; 404 when absent. ' +
            '200 / 403 / 404 / 409.',
    },
    'list-clause-mining-dry-runs': {
        method: 'GET',
        path: '/v1/clause-mining/dry-runs',
        request: ListClauseMiningDryRunsRequestSchema,
        response: ListClauseMiningDryRunsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-16', 'NFR-06', 'NFR-07'],
        notes: 'No parameters; gate contract:read (everyone). active = the one running row or null, ' +
            'pending[] = requested/approved, history[] = the last 20 terminal rows by finished_utc DESC. ' +
            'Read-only, no audit row. 200 / 403.',
    },
    'get-clause-mining-dry-run': {
        method: 'GET',
        path: '/v1/clause-mining/dry-runs/{clause_mining_dry_run_id}',
        request: ClauseMiningDryRunIdRequestSchema,
        response: GetClauseMiningDryRunResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-16', 'NFR-06'],
        notes: 'Path clause_mining_dry_run_id; gate contract:read. One row — polled by the active card for ' +
            'heartbeat and result. Read-only, no audit row. 200 / 400 / 403 / 404.',
    },
    /* --- field accuracy (CLM-107, FR-30 / DR-08; MERGE-ORDER: appended at END) --- */
    'field-accuracy': {
        method: 'GET',
        path: '/v1/admin/field-accuracy',
        request: FieldAccuracyRequestSchema,
        response: FieldAccuracyResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-30', 'DR-08', 'NFR-16', 'NFR-06'],
        notes: 'No parameters; gate admin:configure (Admin only — Legal does not hold it; R-107-6, the first endpoint ' +
            'on that action). One statement restating clm.vw_field_accuracy (0100) from clm.audit_log alone, never ' +
            'field_verification (DR-08), with toScopeSqlPredicate on vw_contract_scope.effective_contract_key inside ' +
            'both aggregates, before GROUP BY (rules 9, 10) — so a practices admin sees figures for their scope ' +
            'only, and scope_kind says so. One row per (field_name, extraction_schema_version, extraction_model) ' +
            '(R-107-1); rate = Verified / (Verified + Corrected), 3 dp, null at n = 0, Rejected counted outside ' +
            'the denominator (R-107-4). Counts are exact and are what any threshold must compare (R-107-8). ' +
            'is_current / is_high_stakes / meets_minimum_sample are set by the handler. No threshold, no ' +
            'pass/fail, no LLM (rule 14), no LIMIT (R-107-9). Read-only, no audit row. 200 / 400 / 403.',
    },
    /* --- base MSA nomination (CLM-111, FR-37 / DR-11; MERGE-ORDER: appended at END) --- */
    'list-base-contracts': {
        method: 'GET',
        path: '/v1/admin/base-contracts',
        request: ListBaseContractsRequestSchema,
        response: ListBaseContractsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-37', 'DR-11', 'NFR-06'],
        notes: 'No parameters; gate clause:review_match (Admin and Legal — D-2.1-11, R-111-6: Legal sees the base ' +
            'read-only). One entry per BASE_CONTRACT_TYPES (MSA only, D-2.1-12): the current nomination (or null) and ' +
            'the superseded versions newest first, each with who nominated and when, who superseded it and when, the ' +
            'contract’s live lifecycle_status and type_mismatch (R-111-9). One SELECT over ' +
            'clm.base_contract_nomination (0101) joined to vw_contract_scope with toScopeSqlPredicate on ' +
            'effective_contract_key (rules 9, 10) — a caller sees only nominations whose contract they may see, and ' +
            'scope_kind says so (R-111-7). No scope-view change (Q-111-1). Read-only, no audit row. 200 / 400 / 403.',
    },
    'nominate-base-contract': {
        method: 'POST',
        path: '/v1/admin/base-contracts/nominate',
        request: NominateBaseContractRequestSchema,
        response: NominateBaseContractResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-37', 'DR-11', 'NFR-06', 'NFR-07'],
        notes: 'Body { contract_type: MSA, contract_key, expected_current_nomination_id | null, note? }; gate ' +
            'admin:configure (Admin only — FR-37). Its own path beside the list, the …/skip · …/reopen convention. ' +
            'One withActor transaction: lock the scoped current row FOR UPDATE, compare it with ' +
            'expected_current_nomination_id, read the contract under scope, check eligibility (R-111-5), supersede ' +
            'the current row (UPDATE superseded_utc / superseded_by_upn — never a DELETE, R-111-3), INSERT … SELECT ' +
            'FROM contract version n+1 under the scope predicate and the same eligibility predicate, read the 0046 ' +
            'receipts back. 404 contract_not_found (absent or out of scope — never 403); 400 field error ' +
            'wrong_contract_type / not_signed / superseded / processing_restricted; 409 base_changed (the expected id ' +
            'is stale, or the 23505 when another admin wins the race on ux_base_contract_nomination_current) / ' +
            'already_current. No write to clm.contract: the nominated contract behaves exactly as before. One ' +
            'Create row, plus one Update on a replacement, each on its own contract’s trail (R-111-8). ' +
            'CLM-118 (R-118-13): the type must be Admin-confirmed — an auto_confirmed type (the file name and the ' +
            'AI agree) is 400 type_not_admin_confirmed; the web confirms it through decide-contract-type first. ' +
            '200 / 400 / 403 / 404 / 409.',
    },
    /* --- base MSA deviation report (CLM-112, FR-38 / NFR-18 / DR-11; MERGE-ORDER: appended at END) --- */
    'list-base-deviation-reports': {
        method: 'GET',
        path: '/v1/contracts/{contract_key}/base-deviation-reports',
        request: ListBaseDeviationReportsRequestSchema,
        response: ListBaseDeviationReportsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-38', 'NFR-18', 'DR-11', 'NFR-06'],
        notes: 'Path contract_key (the subject); gate clause:review_match (Admin and Legal — D-2.1-11, R-112-13). The ' +
            'subject’s refusal (baseDeviationSubjectRefusal, R-112-10) and conversion_caveat; the current base of its ' +
            'type (list-base-contracts’ projection, scoped on the base contract) with both sides’ readiness ' +
            '(not_converted / not_segmented / not_matched / spans_stale — R-112-10, R-112-17), or current_base null when ' +
            'none is visible to the caller (scope_kind says whether that means none exists); and the past runs newest ' +
            'first, each with its pinned base version, nomination_is_current, counts and a live stale flag. Every ' +
            'statement over both contracts is scoped on both effective_contract_keys (rules 9, 10; R-112-12) — a run ' +
            'whose base the caller cannot see is not listed. Counts only: read-only, no audit row (the field-accuracy ' +
            'precedent). 200 / 400 / 403 / 404.',
    },
    'run-base-deviation-report': {
        method: 'POST',
        path: '/v1/contracts/{contract_key}/base-deviation-reports',
        request: RunBaseDeviationReportRequestSchema,
        response: RunBaseDeviationReportResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-38', 'NFR-18', 'DR-11', 'NFR-06', 'NFR-07'],
        notes: 'Path contract_key (the subject), body { nomination_id } — current or superseded, as long as the caller can see ' +
            'it (R-112-11; no expected_* check). Gate clause:review_match. One withActor transaction: read the subject and ' +
            'the nomination under scope; refuse on baseDeviationSubjectRefusal; read both sides’ paragraphs (one ' +
            'statement, no paragraph_embedding), amendment counts and note evidence, scoped on both contracts; verify spans ' +
            'without the FR-29 map (R-112-17: every span inside full_text, and no row provably older than the contract’s ' +
            'converted_utc); computeBaseDeviation (@clm/clause-mining — a pure join, no model, no vector, rule 14); INSERT … ' +
            'SELECT the run under both scope predicates and the eligibility predicate, then the result; require exactly one ' +
            '0046 Create receipt on the subject’s trail. 404 contract_not_found / nomination_not_found (absent or out ' +
            'of scope — never 403, no oracle); 400 field error wrong_contract_type / amendment / same_as_base / ' +
            'base_type_mismatch; 409 not_converted / not_segmented / not_matched / spans_stale and each base_ twin, or ' +
            'contract_changed. 200 / 400 / 403 / 404 / 409.',
    },
    'get-base-deviation-report': {
        method: 'GET',
        path: '/v1/base-deviation-reports/{report_id}',
        request: GetBaseDeviationReportRequestSchema,
        response: GetBaseDeviationReportResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-38', 'NFR-18', 'DR-11', 'NFR-06', 'NFR-07'],
        notes: 'Path report_id; gate clause:review_match. The stored snapshot exactly as computed, beside a live now block: ' +
            'nomination_is_current and base_superseded_utc, text_changed per side (converted_utc moved or is NULL — the ' +
            'page then renders no slices), matches_changed (the inputs digest over both sides’ rows differs), and the ' +
            'current library titles. Writes the gateway’s read audit event (paragraph-level facts about two contracts — ' +
            'the retrieve-contract / clause-matches precedent, NFR-07; R-112-13). 404 report_not_found when the report is ' +
            'absent or either contract is out of scope. 200 / 400 / 403 / 404.',
    },
    /* --- admin-defined extracted fields (CLM-109, FR-33 / DR-09 / NFR-17 / CR-07; MERGE-ORDER: appended at END) --- */
    'list-extraction-fields': {
        method: 'GET',
        path: '/v1/admin/extraction-fields',
        request: ListExtractionFieldsRequestSchema,
        response: ListExtractionFieldsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-33', 'DR-09', 'NFR-17', 'CR-07', 'NFR-13'],
        notes: 'Query { as_of_revision? } — the DR-09 auditor path: the set as it was at an old audit row’s revision. Gate ' +
            'admin:configure (Admin only, R-109-14). Reads clm.extraction_field unscoped — org-wide ' +
            'configuration, the cloudeq_entity precedent — and answers the current revision, every key added by ' +
            'as_of_revision (active or retired) with its definition then, the gapless history newest first, the governed ' +
            'field names (read-only: a governed change is a release) and limits from the same check the save runs over the ' +
            'set (the cap of 10, the 400 bound, 20–500 descriptions, 14 of 16 unions, the schema size). Read-only, no audit ' +
            'row, no LLM (rule 14). 200 / 400 / 403.',
    },
    'change-extraction-field': {
        method: 'POST',
        path: '/v1/admin/extraction-fields/change',
        request: ChangeExtractionFieldRequestSchema,
        response: ChangeExtractionFieldResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-33', 'DR-09', 'NFR-17', 'CR-07', 'NFR-13'],
        notes: 'Body { expected_revision, placement?, change: add | edit | deactivate | reactivate }; gate admin:configure, no ' +
            'Legal co-sign (D-2.1-7, R-109-14). Its own path beside the list, the …/skip · …/nominate convention. In order: ' +
            'placement top_level → 400 top_level_refused naming 16, 14 and CLM-80, before any statement (FR-33); read the ' +
            'current set; apply the change to a copy (404 field_not_found, 409 field_exists / field_retired / ' +
            'field_already_active / field_already_inactive, 400 no_change); check it — key, governed name, value bound 400, ' +
            'description 20–500 and rule 13 / CR-03 tripwires over the description and the key, the cap of 10, union count / ' +
            'optional count / schema size (NFR-17) — and scan the description and the key for a known party name (400 ' +
            'description_names_party / field_key_names_party, R-109-10); then one ' +
            'withActor transaction inserts revision expected_revision + 1 only if it is still the next (409 ' +
            'field_set_changed, also on the 23505 race) and reads the one 0046 Create receipt back. Never an UPDATE or ' +
            'DELETE: the table refuses both, so a retired field’s rows are never rewritten (CR-07). The next extraction ' +
            'reads the new set; no deployment. 200 / 400 / 403 / 404 / 409.',
    },
    /* --- contract type proposals (CLM-110, FR-35 / FR-34 / DR-10; MERGE-ORDER: appended at END) --- */
    'contract-type-proposals': {
        method: 'GET',
        path: '/v1/admin/contract-type-proposals',
        request: ContractTypeProposalsRequestSchema,
        response: ContractTypeProposalsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-35', 'FR-34', 'DR-10', 'CR-08', 'NFR-06'],
        notes: 'No parameters; gate admin:configure (D-110-2: only an Admin decides a type). Read-only, v3 (CLM-118, ' +
            'R-118-16): Type pending agreements with their reasons (ai_sow_needs_number, ai_type_not_applied, ' +
            'awaiting_rerun), name_evidence, auto-confirmed families with a newer disagreeing reading (auto_confirmation, ' +
            'never a decision), an amendment_signals list, and totals for pending, ai_decided, auto_confirmed and ' +
            'amendment_signals — all from the same one statement; a pending agreement’s reading counts as not_compared. ' +
            'v2 (CLM-110, ' +
            'R-110-22): one scoped statement over every contract that is provisional or has a reading, each joined to its ' +
            'latest extraction-pipeline AIExtraction row for classification.contract_type (a LEFT JOIN LATERAL, so an ' +
            'answer outside the vocabulary is seen — ai_no_type, R-110-28), its confirmation row, its root’s primary party ' +
            'and its root’s current-nomination flag; folded in memory. undecided: the provisional agreements by reason — ' +
            'ai_disagrees and ai_no_type, ai_agrees, restricted, no_ai_proposal (the newest 200, with the full count) — ' +
            'each with its amendments’ readings nested (an amendment never stands alone, R-110-15) and the seen block the ' +
            'decision dialog posts back. confirmed_newer_disagreements: confirmed families whose agreement has a newer ' +
            'disagreeing reading. Totals: R-110-7’s proposals = agreements + disagreements + not_compared (proposals on ' +
            'amendments), the per-reason counts, awaiting_ai and confirmed — self-checking refines. Every row and total ' +
            'scoped on root_contract_key before counting (rules 9, 10). Writes nothing: the decision is ' +
            'decide-contract-type. CLM-121 (R-121-15): each undecided row carries auto_approval_sample — the type’s ' +
            'auto-approval sent this AI-decided type to an Admin as its 1-in-5 safety sample — and the totals add ' +
            'auto_approval_samples and policy_approved (of auto_confirmed, approved by policy). 200 / 400 / 403.',
    },
    /* --- verification policy / auto-approval (CLM-108, FR-31 / FR-32 / NFR-16 / CR-06; MERGE-ORDER: appended at END) --- */
    'verification-policy': {
        method: 'GET',
        path: '/v1/admin/verification-policy',
        request: VerificationPolicyRequestSchema,
        response: VerificationPolicyResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-31', 'FR-32', 'NFR-16', 'CR-06', 'NFR-06'],
        notes: 'No parameters; gate admin:configure, and the per-field table needs Scope-All as well (scope.kind is all, ' +
            'R-108-12): a practice-scoped Admin gets 200 with fields: null and fields_withheld: scope_all_required, ' +
            'still sees the settings card, and causes no read of the basis — the check runs before any statement. ' +
            'Settings: current (or null — auto-approval off until the first co-sign, D-2.1-7), pending, history newest ' +
            'first. Fields: the governed list plus every admin key ever added (classification.contract_type never), each ' +
            'with its grain (the latest pipeline extraction; an admin field names its definition revision, R-108-6), the ' +
            'org-wide basis from fn_field_policy_basis (D-107-1; counts exact, rates numeric(4,3) strings, R-108-5), the ' +
            'policy in force (revision 0 = the threshold default; an opt-in’s co-sign and whether it lapsed), ' +
            'effective_state (the pipeline’s decision with the sample step dropped), stale_since_utc, the last 30 days by ' +
            'disposition and auto_approved_total. The grain and disposition reads are scoped on effective_contract_key ' +
            '(rules 9, 10) though only Scope-All reaches them; the basis and the configuration are unscoped by design. ' +
            'Nothing here decides a field. CLM-121: classification.contract_type’s verification_policy rows are the type’s ' +
            'auto-approval switch (contract-type-auto-approval), never listed or acted on here. Read-only, no audit row, ' +
            'no LLM (rule 14). 200 / 400 / 403.',
    },
    'set-field-verification-policy': {
        method: 'POST',
        path: '/v1/admin/verification-policy/field',
        request: SetFieldVerificationPolicyRequestSchema,
        response: SetFieldVerificationPolicyResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-31', 'FR-32', 'NFR-16', 'CR-06', 'NFR-06', 'NFR-07'],
        notes: 'Body { field_name, expected_policy_revision, mode, change_note? }; gate admin:configure plus Scope-All ' +
            '(403 not_authorized, logged scope_all_required, before any statement — R-108-12). The basis is captured ' +
            'server-side at the field’s current grain; the client sends no counts, UPN or object id. Refusals in order: ' +
            '404 field_not_found; 409 field_retired; 400 high_stakes_exempt (D-2.1-5: no policy row on a high-stakes ' +
            'field); 400 no_change; 400 note_required (auto_approve); 409 too_few_decisions (auto_approve below 30 at the ' +
            'current grain, D-2.1-2); 409 policy_changed (revision mismatch, or the 23505 on ' +
            'uq_verification_policy_revision). One withActor transaction inserts revision expected + 1 with the session’s ' +
            'own UPN and object id (tg_verification_policy_append_only checks both, and admin) and reads the 0046 Create ' +
            'receipt back — FR-31’s audit row, carrying the rate and sample size. always_verify and threshold take effect ' +
            'at once; an auto_approve revision is ignored by the decision until a different Legal holder co-signs it ' +
            '(R-108-24), and effective_state is returned as it stands before that. 200 / 400 / 403 / 404 / 409.',
    },
    'cosign-field-verification-policy': {
        method: 'POST',
        path: '/v1/admin/verification-policy/field/cosign',
        request: CosignFieldVerificationPolicyRequestSchema,
        response: CosignFieldVerificationPolicyResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-31', 'FR-32', 'NFR-16', 'CR-06', 'NFR-06', 'NFR-07'],
        notes: 'Body { field_name, policy_revision, decision: cosign | decline, decision_note? }; gate ' +
            'compliance:cosign_verification_settings (Legal only, R-108-11) plus Scope-All — the co-signer agrees to ' +
            'org-wide figures (R-108-12); 403 before any statement otherwise. Refusals in order: 404 field_not_found; 409 ' +
            'policy_changed (not the field’s current revision); 409 not_opt_in; 409 opt_in_lapsed (its basis grain is no ' +
            'longer the latest pipeline grain); 409 cosigner_is_proposer (same UPN in any case, or same object id — a ' +
            'dual-role caller on their own opt-in included; decided on the revision row just read, before any write, with ' +
            'ck_verification_policy_cosign_two_people as the backstop); 400 note_required (decline); 409 already_decided ' +
            '(the 23505). One INSERT … SELECT from the revision, copying field_name and changed_by_* into the composite FK, ' +
            'then the 0046 receipt carrying the legal_reviewer role (TD-01). 200 / 400 / 403 / 404 / 409.',
    },
    'propose-verification-settings': {
        method: 'POST',
        path: '/v1/admin/verification-policy/settings/propose',
        request: ProposeVerificationSettingsRequestSchema,
        response: ProposeVerificationSettingsResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-32', 'NFR-16', 'CR-06', 'NFR-06', 'NFR-07'],
        notes: 'Body { expected_setting_revision, threshold, sample_rate, stale_after_days, proposal_note }; gate ' +
            'admin:configure. Threshold and sample rate are numeric(4,3) strings ("0.900"). Refusals: 409 change_pending ' +
            '(the 23505 on ux_verification_policy_setting_one_pending); 409 settings_changed (expected ≠ current); 400 ' +
            'threshold_out_of_bounds / sample_rate_out_of_bounds / stale_window_out_of_bounds (the database’s sanity bounds, ' +
            'R-108-9); 400 no_change (all three equal to current). Inserts a pending row with supersedes_revision the ' +
            'expected one and the session’s own UPN and object id (the guard checks both, and admin); returns the 0046 ' +
            'Create receipt. All three values need the co-sign (R-108-10). The pre-filled values live on the page, not ' +
            'here. 200 / 400 / 403 / 409.',
    },
    'withdraw-verification-settings': {
        method: 'POST',
        path: '/v1/admin/verification-policy/settings/withdraw',
        request: WithdrawVerificationSettingsRequestSchema,
        response: WithdrawVerificationSettingsResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-32', 'NFR-16', 'CR-06', 'NFR-06', 'NFR-07'],
        notes: 'Body { setting_change_id, note? }; gate admin:configure — any Admin, not only the proposer. Only a pending row ' +
            '(409 not_pending, also for an unknown id). A guarded UPDATE to withdrawn recording the session’s own UPN and ' +
            'object id as the decider (the guard requires admin); the 0046 Update receipt. 200 / 400 / 403 / 409.',
    },
    'verification-settings': {
        method: 'GET',
        path: '/v1/admin/verification-policy/settings',
        request: VerificationSettingsRequestSchema,
        response: VerificationSettingsResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-32', 'NFR-16', 'CR-06', 'NFR-06'],
        notes: 'No parameters; gate compliance:cosign_verification_settings (Legal only — Admin does not hold it, R-108-11). ' +
            'The settings card (current, pending, history) and caller_is_proposer (UPN case-insensitively, or object id). ' +
            'impact (fields that auto-approve now and at the pending values) and pending_opt_ins (current auto_approve ' +
            'revisions with no decision, each with its recorded basis and caller_is_setter) carry org-wide counts, so they ' +
            'are computed only for a Scope-All caller and are null otherwise (R-108-12). Read-only, no audit row. ' +
            '200 / 400 / 403.',
    },
    'cosign-verification-settings': {
        method: 'POST',
        path: '/v1/admin/verification-policy/settings/cosign',
        request: CosignVerificationSettingsRequestSchema,
        response: CosignVerificationSettingsResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-32', 'NFR-16', 'CR-06', 'NFR-06', 'NFR-07'],
        notes: 'Body { setting_change_id, decision: cosign | decline, seen: { threshold, sample_rate, stale_after_days }, ' +
            'decision_note? }; gate compliance:cosign_verification_settings. 400 note_required on a decline, before any ' +
            'statement. Then the pending row is read and, before any write: 409 not_pending; 409 cosigner_is_proposer (same ' +
            'UPN in any case, or same object id — a dual-role caller on their own proposal included; ' +
            'ck_verification_policy_setting_two_people is the backstop); 409 stale_view (seen differs: the co-signer attests ' +
            'to what they read). A guarded UPDATE … WHERE status = pending AND proposer ≠ caller AND the seen values match, ' +
            'setting decided_by_* from the session and, on cosign, setting_revision = max + 1 (a 0-row UPDATE after those ' +
            'checks is a lost race, reported not_pending); the 0046 Update receipt carries the legal_reviewer role (TD-01). ' +
            'D-2.1-7: only now does the threshold take effect. 200 / 400 / 403 / 409.',
    },
    /* --- CLM-127, D-127-1: flag-gated self-certify, appended immediately after the CLM-108 block
       above (the registry adjacency test tolerates this — it asserts adjacency, not tail
       position). Do not reorder or move these two relative to the block above. --- */
    'self-certify-verification-settings': {
        method: 'POST',
        path: '/v1/admin/verification-policy/settings/self-certify',
        request: SelfCertifyVerificationSettingsRequestSchema,
        response: SelfCertifyVerificationSettingsResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-31', 'NFR-16', 'CR-06', 'NFR-06', 'NFR-07'],
        notes: 'Body identical to propose-verification-settings (a type alias, R-127-14); gate admin:configure. ' +
            'CLM-127, D-127-1: refused 403 self_certify_disabled when CLM_API_VERIFICATION_POLICY_ALLOW_SELF_CERTIFY ' +
            'is unset (the default) — checked first, before Scope-All, before any statement; then 403 ' +
            'scope_all_required unless scope.kind is all (the ticket names "one Scope-All Admin", no existing ' +
            'settings-path route checks this today, so there is no precedent this copies). One withActor transaction: ' +
            'proposeSettingsQuery (unchanged), then cosignSettingsQuery with decision cosign, isSelfCertified true, the ' +
            'same session actor as decider and proposer. The database CHECKs ' +
            '(ck_verification_policy_setting_two_people) require the identical person when the marker is true, so the ' +
            'row can never be mistaken for a genuine co-sign. Reversible: the flag off stops the action from being ' +
            'reachable again; rows already self-certified stay exactly as written. Legal is never consulted — this is ' +
            'the point. 200 / 400 / 403 / 409.',
    },
    'self-certify-field-verification-policy': {
        method: 'POST',
        path: '/v1/admin/verification-policy/field/self-certify',
        request: SelfCertifyFieldVerificationPolicyRequestSchema,
        response: SelfCertifyFieldVerificationPolicyResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-31', 'FR-32', 'NFR-16', 'CR-06', 'NFR-06', 'NFR-07'],
        notes: 'Body { field_name, expected_policy_revision, change_note } — no mode: fixed server-side to auto_approve, ' +
            'the only mode a co-sign ever applies to (R-108-3). Gate admin:configure; CLM-127, D-127-1: 403 ' +
            'self_certify_disabled when the flag is unset, then Scope-All (R-108-12, as set-field-verification-policy ' +
            'checks it), then the same field-exists / not-retired / not-high-stakes / minimum-sample checks that route ' +
            'makes. One withActor transaction, interleaved (insertPolicyQuery, its own 0046 receipt read, then ' +
            'insertCosignQuery with isSelfCertified true, then its own receipt read) — both inserts are action_type ' +
            'Create under one correlation id, which is why readReceipt (shared by every endpoint in this file) was ' +
            'generalised to identify its own row among several candidates rather than assume exactly one row comes ' +
            'back (R-127-20). ck_verification_policy_cosign_two_people requires the identical person when the marker is ' +
            'true. A self-certified opt-in is never left pending: it never appears in Legal’s pending_opt_ins, because ' +
            'the decide half runs in the same transaction as the propose half (R-127-18). 200 / 400 / 403 / 404 / 409.',
    },
    /* --- contract type decision (CLM-110, FR-35 / FR-34 / FR-36 / DR-10 / CR-08 / DR-01; MERGE-ORDER: appended at END) --- */
    'decide-contract-type': {
        method: 'POST',
        path: '/v1/contracts/{contract_key}/type',
        request: DecideContractTypeRequestSchema,
        response: DecideContractTypeResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-35', 'FR-34', 'FR-36', 'DR-10', 'CR-08', 'DR-01', 'NFR-06', 'NFR-07'],
        notes: 'Path contract_key; body { contract_type (MSA | NDA | SOW | OTHER), sow_number?, note?, seen }; gate ' +
            'admin:configure (D-110-2) — contributor, reader and Legal get 403 before any statement. The unit is the ' +
            'agreement family (the root and every row with its root_contract_key). One withActor transaction, ' +
            'source_component contract-type-decision, every statement but two set_config calls scoped on ' +
            'root_contract_key: (a) lock the family FOR UPDATE with each row’s latest reading; (b) the current base; (c) ' +
            'the SOW number in use outside the root’s agreement_family_id, only into SOW; (d) one HumanVerification row on ' +
            'the root, field classification.contract_type, verify.ts’s new_value shape plus decision_kind, ' +
            'counted_for_accuracy, the provisional type and the family — seen_extracted_value and the reading’s schema ' +
            'version only when counted (an agreement’s first decision after its own latest reading, R-110-18); (e) ' +
            'clm.allow_superseded_write on only when a family row is Superseded; (f) one UPDATE of the whole family — ' +
            'type, source (R-110-20; amendments inherited), confirmed, the decision row, SOW numbers in or cleared — ' +
            'whose row count must equal (a)’s; (e′) the hatch off; (g) the lineage notes. Refusals in order: 404 ' +
            'contract_not_found (out of scope too, never 403); 409 amendment_follows_agreement; 409 awaiting_ai_proposal; ' +
            '409 stale_view (seen differs from the locked rows); 400 no_change; 400 sow_number_required / 422 ' +
            'sow_number_shape / 400 sow_number_not_applicable; 409 sow_number_in_use; 409 current_base_contract; 409 ' +
            'family_changed. Never writes contract_key (FR-35), never touches the notification ledger (rule 15), writes ' +
            'no field_verification row. The receipt is (d). CLM-118 (D-110-3, R-118-10): a Type pending root is decided ' +
            'like any other — decision_kind typed when the Admin replaces the placeholder with a type that is not the ' +
            'reading, the root’s source ai_proposed when the reading is taken and manual otherwise, Corrected when ' +
            'uncounted; an auto_confirmed root may be confirmed (no_change fires on confirmed only) and becomes ' +
            'confirmed. Every decision row records type_case and name_evidence. CLM-121 (R-121-10): a counted decision ' +
            'on an agreement whose latest reading was an auto-approval safety sample also records safety_sample: true ' +
            '(correcting one pauses auto-approval); a policy-approved root may be confirmed or corrected like a 1a one. ' +
            '200 / 400 / 403 / 404 / 409 / 422.',
    },
    /* --- vendor-list import (CLM-113 + CLM-114, FR-39 / FR-40 / FR-40a / DR-12 / NFR-19; MERGE-ORDER: appended at END) --- */
    'plan-vendor-import': {
        method: 'POST',
        path: '/v1/parties/vendor-imports/plan',
        request: PlanVendorImportRequestSchema,
        response: PlanVendorImportResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-39', 'FR-40', 'FR-40a', 'DR-12', 'NFR-19'],
        notes: 'multipart/form-data with the two server-named parts `metadata` (application/json, validated against the ' +
            'request schema: the sheet descriptor and an optional cloudeq_entity_key) and `document` (the .xlsx or .csv ' +
            'bytes). Gate party:import_vendors (Admin only, R-113-20), checked before the body is decoded, so a ' +
            'non-Admin\'s file is never parsed. Verifies the descriptor (size, sha256), sniffs the format from the bytes ' +
            '(media_type_mismatch, ole_container, macro_enabled_workbook, …), reads the first sheet through five ZIP parts ' +
            'with no new library (R-113-2), finds the header and the entity in the preamble (R-113-3) and classifies every ' +
            'row (§3.4) against the parties, aliases and references read unscoped (corpus reference data) and, for the ' +
            'FR-29 counts only, the scoped vendor-import:affected-contracts. Writes nothing and persists no plan (rule 8); ' +
            'the gateway\'s request event is its only audit row. No entity detected and none given: 200 with ' +
            'entity.required and no rows. 400 file refusals carry field_errors[0] = { path: file, message: ' +
            '"<refusal>: <detail>" }; a file over the import bound is a bare 413 payload_too_large; 422 entity_unknown / ' +
            'entity_contradicts_sheet at cloudeq_entity_key. No AI. 200 / 400 / 403 / 413 / 422.',
    },
    'execute-vendor-import': {
        method: 'POST',
        path: '/v1/parties/vendor-imports/execute',
        request: ExecuteVendorImportRequestSchema,
        response: ExecuteVendorImportResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-39', 'FR-40', 'FR-40a', 'DR-12', 'NFR-19', 'NFR-07'],
        notes: 'multipart/form-data with the same two parts: the same file re-sent, with planned_sha256 (the plan\'s ' +
            'file.sha256), cloudeq_entity_key, plan_digest and the approved rows. Gate party:import_vendors (Admin only), ' +
            'before the body is decoded. Descriptor verified (400), then the bytes\' digest against planned_sha256 (409 ' +
            'file_changed). One withActor transaction under pg_advisory_xact_lock(hashtext(\'clm:party-registry\')) — the ' +
            'lock register-vendor-party and manage-party-alias propose/approve take too (R-113-11): the plan is recomputed ' +
            'and compared by plan_digest (409 plan_changed); 422 entity_unknown / entity_contradicts_sheet / ' +
            'individual_test_weaker_unconfirmed / row_not_in_plan / action_not_offered / link_target_not_offered / ' +
            'short_name_unconfirmed / shared_alias_unconfirmed / individual_reason_required at the row\'s path; nothing ' +
            'approved to write → 200 with batch null and nothing written (R-113-13). Otherwise one batch row, then per ' +
            'approved row in sheet order: the party (sor_system CLM, key minted avoiding RESERVED_VENDOR_KEYS), its names ' +
            'as confirmed Manual aliases carrying import_batch_id, the (entity, vendor number) reference with its address; ' +
            'a pending proposal on the row\'s own party confirmed in place (R-114-2). Every write audited by its 0046 ' +
            'trigger, the count read back and required exact (R-113-22). fr29 counts the scoped affected contracts and, ' +
            'unscoped, the existing parties with contracts whose names changed — the operator notice follows the latter ' +
            '(R-114-6). All or nothing; a 23505 is 409 registry_changed. Nothing is ever deactivated (D-2.1-10). No AI. ' +
            '200 / 400 / 403 / 409 / 413 / 422.',
    },
    'list-vendor-imports': {
        method: 'GET',
        path: '/v1/parties/vendor-imports',
        request: ListVendorImportsRequestSchema,
        response: ListVendorImportsResponseSchema,
        writes_audit_row: false,
        /*
         * A recorded departure from spec §4.3's single list (FR-39, FR-40, FR-40a,
         * DR-12, NFR-19 on all three): the list reads batch receipts (FR-39) and
         * the bounds (NFR-19) and writes no alias, address or reference, so it
         * cites those two only. Execute adds NFR-07 for its per-write audit rows,
         * the nominate-base-contract precedent.
         */
        requirement_refs: ['FR-39', 'NFR-19'],
        notes: 'Query { cloudeq_entity_key?, page? }; gate party:import_vendors (Admin only). The batch history newest first, ' +
            'keyset on (executed_utc, import_batch_id), and limits — the effective import bound, the row cap and the ' +
            'request cap — which the page reads on load to refuse an oversized file before the first upload (R-113-21). ' +
            'A reference-table read with no contract_key: no scope predicate and no audit row (the list-cloudeq-entities ' +
            'precedent). 200 / 400 / 403.',
    },
    /* --- possibly an amendment (CLM-118, DR-02 / B13; MERGE-ORDER: appended at END) --- */
    'dismiss-amendment-signal': {
        method: 'POST',
        path: '/v1/contracts/{contract_key}/amendment-signal/dismiss',
        request: DismissAmendmentSignalRequestSchema,
        response: DismissAmendmentSignalResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['DR-02', 'NFR-06', 'NFR-07'],
        notes: 'Path contract_key; empty body; gate admin:configure. One withActor transaction, source_component ' +
            'amendment-signal, every statement scoped on root_contract_key: lock the contract FOR UPDATE; set ' +
            'amendment_signal_dismissed_utc and _by_upn (the session actor) only while the flag predicate holds (an ' +
            'agreement with no amends or supersedes edge, not Superseded, not dismissed, with an amendment word in its ' +
            'name or a reading’s reference); read back the tg_contract_audit Update row as the receipt (its absence is a ' +
            '500). The type is untouched; linking is CLM-120. 404 contract_not_found (out of scope too, never 403); 409 ' +
            'no_amendment_signal. 200 / 400 / 403 / 404 / 409.',
    },
    /* --- fix links after upload (CLM-120, D-110-4 point 5, FR-08 / DR-02 / DR-03; MERGE-ORDER: appended at END) --- */
    'contract-links': {
        method: 'GET',
        path: '/v1/contracts/{contract_key}/links',
        request: ContractLinksRequestSchema,
        response: ContractLinksResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-08', 'DR-02', 'DR-03', 'NFR-06'],
        notes: 'Path contract_key; optional query target_contract_key (one candidate by key, past the cap); gate ' +
            'admin:configure. Read-only: the contract’s links (its own three edges by key, with type and lifecycle only ' +
            'when in scope; the rows pointing at it — replacement, amendments, children — only when in scope), what the ' +
            'action may do and why not, and the candidates — the upload’s candidate list keyed on the contract’s primary ' +
            'party (≤ 100) — each with the links the POST would accept and their effects, computed by the same planner. ' +
            'Every statement scoped on root_contract_key. 404 contract_not_found (out of scope too, never 403). ' +
            '200 / 400 / 403 / 404.',
    },
    'set-contract-link': {
        method: 'POST',
        path: '/v1/contracts/{contract_key}/links',
        request: SetContractLinkRequestSchema,
        response: SetContractLinkResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-08', 'DR-02', 'DR-03', 'FR-35', 'NFR-06', 'NFR-07'],
        notes: 'Path contract_key; body { link: child_of | amends | supersedes, target_contract_key | null (child_of only: ' +
            'remove the parent MSA), note?, seen }; gate admin:configure. One withActor transaction, source_component ' +
            'contract-links, every statement but the active-run read scoped on root_contract_key: lock the link set (the ' +
            'contract’s and the target’s agreement families, its parent, its replacement and the rows beneath it) FOR ' +
            'UPDATE ordered by amendment_depth, contract_key; read it again in a new statement; plan (pure — intake’s ' +
            'rules, seen, Rule 3); only when the act newly restricts, the active pipeline runs; one UPDATE (child_of: the ' +
            'edge and the hierarchy depths beneath; amends: edge, depth, root, family, the agreement’s type and ' +
            'confirmation, SOW number, owner, classification, category and restriction; supersedes: edge, depth, version ' +
            'and the family of the contract and its amendments, the target flipped Superseded by the trigger); for amends ' +
            'the agreement’s other parties; one act row (Amendment, Supersession or Update) by INSERT … SELECT FROM ' +
            'contract, its RETURNING the receipt. Refusals in order: 404 contract_not_found (the contract, the target or ' +
            'the old target — out of scope too, never 403); 409 stale_view; 409 superseded_read_only; 400 no_change; 409 ' +
            'amendment_follows_agreement; 409 supersession_is_one_way; 400 relationship_not_valid; 400 ' +
            'party_follows_agreement; 400 relationship_cycle; 409 has_amendments / has_children; 409 ' +
            'current_base_contract; 409 agreement_restricted_contract_read; 409 pipeline_run_active; 409 links_changed; ' +
            '409 lineage_outside_scope. Never writes contract_key, never a lifecycle_status but the flip’s, never a ' +
            'Superseded row; the amends statement is FR-35’s third writer of contract_type (the agreement’s type); no ' +
            'model call; never counted. 200 / 400 / 403 / 404 / 409.',
    },
    /* --- contract type auto-approval (CLM-121, FR-32 / NFR-16 / CR-08 / FR-34; MERGE-ORDER: appended at END) --- */
    'contract-type-auto-approval': {
        method: 'GET',
        path: '/v1/admin/contract-type-auto-approval',
        request: ContractTypeAutoApprovalRequestSchema,
        response: ContractTypeAutoApprovalResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-32', 'NFR-16', 'CR-08', 'FR-34', 'NFR-06', 'NFR-07'],
        notes: 'No parameters; gate admin:configure. The contract type’s auto-approval switch (D-110-4, D-110-3 point 3): ' +
            'its classification.contract_type verification_policy revisions newest first (threshold = on at the grain ' +
            'recorded, always_verify = off, no row = off), shown to every Admin. Everything computed on the org-wide ' +
            'figure needs Scope-All as well, checked before any other statement (R-121-16): a practice-scoped Admin gets ' +
            '200 with withheld: scope_all_required and every figure null, each revision’s recorded basis included. For a ' +
            'Scope-All caller: the grain of the latest pipeline reading of the type (scoped on effective_contract_key); ' +
            'the counted figure there — only decisions on contracts whose file name gave no type (type_case no_name_type, ' +
            'or name_evidence_unknown on a person’s type, the AI’s or a placeholder OTHER, 6b) — and "AI against the file ' +
            'name" (disagree, display only); the state (switched_off, ' +
            'not_read, lapsed, paused_by_correction, too_few_decisions, stale, below_threshold, on), the pause, the sticky ' +
            'lapse and stale_since_utc; whether switching on is offered and why not; and the type step’s activity (scoped ' +
            'on root_contract_key). The figures are fixed — strictly above 0.900 over at least 30 counted decisions, 1 in 5 ' +
            'sampled, 90 days — never settings, and there is no Legal co-sign (1943297). Read-only, no audit row, no LLM ' +
            '(rule 14). 200 / 400 / 403.',
    },
    'set-contract-type-auto-approval': {
        method: 'POST',
        path: '/v1/admin/contract-type-auto-approval',
        request: SetContractTypeAutoApprovalRequestSchema,
        response: SetContractTypeAutoApprovalResponseSchema,
        writes_audit_row: true,
        requirement_refs: ['FR-32', 'NFR-16', 'CR-08', 'FR-34', 'NFR-06', 'NFR-07'],
        notes: 'Body { switch: on | off, expected_policy_revision, change_note? }; gate admin:configure plus Scope-All (403 ' +
            'before any statement, R-121-13) — an Admin alone, no Legal co-sign (Gerry, 1943297). Refusals in order: 400 ' +
            'note_required (on with no note); 409 policy_changed (expected ≠ current, or the 23505 on ' +
            'uq_verification_policy_revision); 400 no_change (off while off; on while on at the current grain, neither ' +
            'paused nor lapsed — a pause or a sticky lapse is resumed only by switching on again); for on, 409 ' +
            'no_reading_grain, 409 too_few_decisions and 409 not_above_threshold (strictly above 0.900 over at least 30 ' +
            'counted decisions at the current grain; fixed, never settings). One withActor transaction under ' +
            'source_component verification-policy-admin inserts the type’s next verification_policy revision — threshold ' +
            'or always_verify, the 6b basis captured server-side, the session’s own UPN and object id ' +
            '(tg_verification_policy_append_only checks both, and admin; ck_verification_policy_contract_type backstops ' +
            'the figures) — and reads the 0046 Create receipt back: FR-31’s audit row, naming the Admin, the figure and ' +
            'the grain. A type already approved stays approved when the switch goes off. 200 / 400 / 403 / 409.',
    },
    /* --- field applicability by contract type (CLM-133, FR-30 amendment / D-131-1; MERGE-ORDER: appended at END) --- */
    'field-applicability-by-type': {
        method: 'GET',
        path: '/v1/admin/field-applicability-by-type',
        request: FieldApplicabilityByTypeRequestSchema,
        response: FieldApplicabilityByTypeResponseSchema,
        writes_audit_row: false,
        requirement_refs: ['FR-30', 'DR-08', 'NFR-06'],
        notes: 'No parameters; gate admin:configure (same gate as field-accuracy - same page). One statement over ' +
            'field_verification joined to contract, with toScopeSqlPredicate on vw_contract_scope.effective_contract_key ' +
            'inside both aggregates, before GROUP BY (rules 9, 10) - so a practices admin sees figures for their scope ' +
            'only, and scope_kind says so. One row per (field_name, contract_type) present in scope, for every governed ' +
            'field except personal_data.contains_personal_data (a NOT NULL boolean, meaningless as a fill rate) and ' +
            'every ratified contract type (MSA, NDA, SOW). total_contracts counts every contract of that type in scope; ' +
            'measured_contracts counts only those with a field_verification row for the field - one never extracted is ' +
            'excluded entirely, never counted as null (D-131-1 section 5). populated_rate is populated divided by ' +
            'measured_contracts, null exactly at measured_contracts = 0, never 0. is_high_stakes is set by the handler ' +
            'from the same HIGH_STAKES_FIELDS set field-accuracy badges. No applies/does-not-apply flag, no schema ' +
            'change, no enforcement - a measurement a person reads, same posture as field-accuracy. No LLM (rule 14), ' +
            'no LIMIT (rule-17 volume). Read-only, no audit row. 200 / 400 / 403.',
    },
};
/** The seven endpoints CLM-19 names, distinguished from their read-side companions. */
export const CORE_ENDPOINT_NAMES = [
    'search',
    'retrieve-contract',
    'upload',
    'manage-clause',
    'resolve-party',
    'verify-field',
    'acknowledge-notification',
];
//# sourceMappingURL=registry.js.map